import { CommonModule } from '@angular/common';
import * as i1$2 from '@angular/common/http';
import { HttpHeaders, HTTP_INTERCEPTORS } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import * as i2 from '@auth0/angular-jwt';
import { JwtHelperService, JWT_OPTIONS } from '@auth0/angular-jwt';
import { isObservable, of } from 'rxjs';
import { take, retryWhen, delay, last, catchError, map } from 'rxjs/operators';
import * as i1 from '@skykick/caching';
import * as i1$1 from 'ngx-cookie-service';

const Default_TTL = 300_000;
/**
 * This class is a wrapper around the CacheService which takes
 * an Observable<T> for the set function and returns an Observable<T> for get and getOrCreate.
 *
 * The underlying cache, however, only stores the actual value if it has been returned, not
 * the observable.  If the source Observable used to set has not yet emitted, then the Observable
 * itself is stored in the underlying cache until the source emits.
 */
class ObservableCacheService {
    constructor(cacheService) {
        this.pendingObservables = {};
        this.cache = cacheService
            .root()
            .withNamespace('ObservableCache');
    }
    set(key, source, ttl = Default_TTL) {
        if (isObservable(source)) {
            this.pendingObservables[key] = source.pipe(take(1));
            this.cache.remove(key);
            source.pipe(take(1)).subscribe(val => {
                delete this.pendingObservables[key];
                this.cache.set(key, val, ttl);
            });
            return source.pipe(take(1));
        }
        else {
            if (this.pendingObservables.hasOwnProperty(key)) {
                delete this.pendingObservables[key];
            }
            this.cache.set(key, source, ttl);
            return of(source);
        }
    }
    get(key) {
        if (this.pendingObservables.hasOwnProperty(key)) {
            return this.pendingObservables[key];
        }
        const cacheValue = this.cache.get(key);
        return undefined === cacheValue || null === cacheValue
            ? cacheValue
            : of(cacheValue);
    }
    /**
     * Return an observable of the cached item, or, if it doesn't exist,
     * create it by calling the createCallback
     * @param createCallback a callback function that will return an Observable<T> which
     * emits the item to be cached.
     */
    getOrCreate(key, createCallback, ttl = Default_TTL) {
        const cacheValue = this.get(key);
        return (undefined === cacheValue || null === cacheValue)
            ? this.set(key, createCallback(), ttl)
            : this.get(key);
    }
    remove(key) {
        this.cache.remove(key);
    }
    clear() {
        this.cache.clear();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: ObservableCacheService, deps: [{ token: i1.CacheService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: ObservableCacheService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: ObservableCacheService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.CacheService }] });

/**
 * This Provider exposes a contract with methods in order to get information about
 * the current Identity context within the application
 */
class AbstractIdentityProvider {
}

class CrossSiteInterceptor {
    constructor(authService) {
        this.authService = authService;
    }
    intercept(req, next) {
        //these are currently the only domains that would need the "X-XSRF-TOKEN" added to the headers
        const url = req.url;
        const domainRegExp = /(skykick\.(com|dev)|localhost(:\d{4})?$)/;
        const shouldIntercept = domainRegExp.test(url);
        if (!shouldIntercept) {
            return next.handle(req);
        }
        try {
            const token = this.authService.getCSRFToken();
            if (token) {
                const reqWithXSRF = req.clone({
                    setHeaders: { 'X-XSRF-TOKEN': '' + token },
                    withCredentials: true
                });
                return next.handle(reqWithXSRF);
            }
        }
        catch (e) {
        }
        return next.handle(req);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: CrossSiteInterceptor, deps: [{ token: AbstractIdentityProvider }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: CrossSiteInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: CrossSiteInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: AbstractIdentityProvider }] });

class SkyKickJwtClaims {
    constructor(jwtClaims) {
        this.jwtClaims = jwtClaims;
    }
    get nickname() {
        return this.jwtClaims.nickname;
    }
    get name() {
        return this.jwtClaims.name;
    }
    get picture() {
        return this.jwtClaims.picture;
    }
    get updated_at() {
        return this.jwtClaims.updated_at;
    }
    get iss() {
        return this.jwtClaims.iss;
    }
    get sub() {
        return this.jwtClaims.sub;
    }
    get aud() {
        return this.jwtClaims.aud;
    }
    get iat() {
        return this.jwtClaims.iat;
    }
    get exp() {
        return this.jwtClaims.exp;
    }
    get csrfid() {
        return this.jwtClaims.csrfid;
    }
    /**
     * A getter for SkyKick specific claims that all start with 'https://skykick.com/'
     * @param prop - the property to fetch
     */
    claim(prop) {
        return this.jwtClaims[`https://skykick.com/${prop}`];
    }
}

const SkyKickAuthCookie = 'sk_id_token';
/**
 * This service provides an identity token to the consumer
 */
class IdentityTokenProvider {
    constructor(cookieService) {
        this.cookieService = cookieService;
    }
    getToken() {
        return this.cookieService.get(SkyKickAuthCookie);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: IdentityTokenProvider, deps: [{ token: i1$1.CookieService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: IdentityTokenProvider, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: IdentityTokenProvider, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$1.CookieService }] });

class SkyKickIdentityProvider extends AbstractIdentityProvider {
    constructor(identityTokenProvider, jwtHelperService) {
        super();
        this.identityTokenProvider = identityTokenProvider;
        this.jwtHelperService = jwtHelperService;
    }
    /**
     * @inheritdoc
     */
    getPicture() {
        return this.claims.picture;
    }
    /**
     * @inheritdoc
     */
    isValid() {
        const token = this.identityTokenProvider.getToken();
        return token && !this.jwtHelperService.isTokenExpired(token);
    }
    /**
     * @inheritdoc
     */
    getIssuer() {
        return this.claims.iss;
    }
    /**
     * @inheritdoc
     */
    getCSRFToken() {
        return this.getClaim('csrfid');
    }
    /**
     * @inheritdoc
     */
    getClaim(property) {
        return this.claims.claim(property);
    }
    get claims() {
        if (!this.cache) {
            const idToken = this.identityTokenProvider.getToken();
            if (!idToken) {
                throw new Error("No id_token was found. Unable to generate Identity");
            }
            this.cache = new SkyKickJwtClaims(this.jwtHelperService.decodeToken(idToken));
        }
        return this.cache;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: SkyKickIdentityProvider, deps: [{ token: IdentityTokenProvider }, { token: i2.JwtHelperService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: SkyKickIdentityProvider, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: SkyKickIdentityProvider, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: IdentityTokenProvider }, { type: i2.JwtHelperService }] });

class ILicenseAssignmentServiceClient {
}

class Logger {
    info(message) {
        console.info(this.formatMessage(message));
    }
    warning(message) {
        console.warn(this.formatMessage(message));
    }
    error(message) {
        console.error(this.formatMessage(message));
    }
    debug(message) {
        console.debug(this.formatMessage(message));
    }
    formatMessage(message) {
        return "[" + new Date() + "] " + message;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: Logger, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: Logger }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: Logger, decorators: [{
            type: Injectable
        }] });

var LicenseAssignmentType;
(function (LicenseAssignmentType) {
    LicenseAssignmentType[LicenseAssignmentType["User"] = 10] = "User";
    LicenseAssignmentType[LicenseAssignmentType["Feature"] = 20] = "Feature";
    LicenseAssignmentType[LicenseAssignmentType["Allocable"] = 30] = "Allocable";
})(LicenseAssignmentType || (LicenseAssignmentType = {}));

/**
 * This Provider exposes a contract with methods in order to get information about
 * the current licenses for a given partner
 */
class LicenseAssignmentProvider {
    constructor(licenseAssignmentServiceClient, logger) {
        this.licenseAssignmentServiceClient = licenseAssignmentServiceClient;
        this.logger = logger;
    }
    /**
     * Get the users assigned licenses for a given partner
     */
    async getLicenses(partnerId, userId) {
        try {
            const response = await this.licenseAssignmentServiceClient.getLicenseAssignments(partnerId, userId);
            if (response.status === 200) {
                const result = this.mapResult(response.body);
                return result;
            }
            else {
                this.handleBadResult(response);
            }
        }
        catch (error) {
            this.logger.error(error);
            return [];
        }
    }
    async getLicensesByProduct(partnerId, productId) {
        try {
            const response = await this.licenseAssignmentServiceClient.getLicenseAssignmentsByProduct(partnerId, productId);
            if (response.status === 200) {
                const result = this.mapResult(response.body);
                return result;
            }
            else {
                this.handleBadResult(response);
            }
        }
        catch (error) {
            this.logger.error(error);
            return [];
        }
    }
    /**
     * Get the users assigned licenses for a given partner and license type
     */
    async getLicensesByProductAndType(partnerId, productId, licenseTypeId) {
        try {
            const response = await this.licenseAssignmentServiceClient.getLicenseAssignmentsByProductAndType(partnerId, productId, licenseTypeId);
            if (response.status === 200) {
                const result = this.mapResult(response.body);
                return result;
            }
            else {
                this.handleBadResult(response);
            }
        }
        catch (error) {
            this.logger.error(error);
            return [];
        }
    }
    // TODO: What's the preferred way for us to handle a blow up. (we should probably retry)
    handleBadResult(response) {
        if (response.status >= 400 && response.status < 600) {
            throw new Error(`HTTP call failed with Status Code: ${response.status}, ${response.statusText}`);
        }
    }
    mapResult(responseBody) {
        var userAssignments = responseBody.userAssignments.map(assignment => {
            return {
                productId: assignment.productId,
                licenseTypeId: assignment.licenseTypeId,
                licenseType: LicenseAssignmentType.User
            };
        });
        var licenseAssignments = responseBody.featureAssignments.map(assignment => {
            return {
                productId: assignment.productId,
                licenseTypeId: assignment.licenseTypeId,
                licenseType: LicenseAssignmentType.Feature
            };
        });
        return userAssignments.concat(licenseAssignments);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: LicenseAssignmentProvider, deps: [{ token: ILicenseAssignmentServiceClient }, { token: Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: LicenseAssignmentProvider }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: LicenseAssignmentProvider, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: ILicenseAssignmentServiceClient }, { type: Logger }] });

class LicenseAssignmentServiceClient extends ILicenseAssignmentServiceClient {
    constructor(http, baseUrl, urlSuffix, identityTokenProvider) {
        super();
        this.http = http;
        this.baseUrl = baseUrl;
        this.urlSuffix = urlSuffix;
        this.identityTokenProvider = identityTokenProvider;
        this.baseUrl = `${this.baseUrl}/${this.urlSuffix}`;
    }
    /**
     * Makes a call to the /partner/{partnerId} endpoint
     */
    getLicenseAssignments(partnerId, userId) {
        const url = this.baseUrl + '/partner/' + partnerId + '/user/' + userId;
        const result = this.http.get(url, { observe: 'response', headers: this.getHeaders(), withCredentials: true }).toPromise();
        return result;
    }
    /**
     * Makes a call to the /partner/{partnerId}/product/{productId} endpoint
     */
    getLicenseAssignmentsByProduct(partnerId, productId) {
        const url = this.baseUrl + '/partner/' + partnerId + '/product/' + productId;
        const result = this.http.get(url, { observe: 'response', headers: this.getHeaders(), withCredentials: true }).toPromise();
        return result;
    }
    /**
     * Makes a call to the /partner/{partnerId}/product/{productId}/licenseType/{licenseTypeId} endpoint
     */
    getLicenseAssignmentsByProductAndType(partnerId, productId, licenseTypeId) {
        const url = this.baseUrl + '/partner/' + partnerId + '/product/' + productId + '/licenseType/' + licenseTypeId;
        const result = this.http.get(url, { observe: 'response', headers: this.getHeaders(), withCredentials: true }).toPromise();
        return result;
    }
    /**
     * Returns a default set of headers for auth and content
     */
    getHeaders() {
        return new HttpHeaders({
            'Content-Type': 'application/json'
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: LicenseAssignmentServiceClient, deps: [{ token: i1$2.HttpClient }, { token: 'LicenseAssignmentServiceBaseUrl' }, { token: 'LicenseAssignmentServiceUrlSuffix' }, { token: IdentityTokenProvider }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: LicenseAssignmentServiceClient, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: LicenseAssignmentServiceClient, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$2.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['LicenseAssignmentServiceBaseUrl']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['LicenseAssignmentServiceUrlSuffix']
                }] }, { type: IdentityTokenProvider }] });

class LicenseBalanceServiceClient {
    constructor(client, baseUrl, urlSuffix, logger) {
        this.client = client;
        this.baseUrl = baseUrl;
        this.urlSuffix = urlSuffix;
        this.logger = logger;
        this.baseUrl = `${this.baseUrl}/${this.urlSuffix}`;
    }
    /**
     * Get all the license balances for the partner.
     * retries 3 times, with 500ms delay.
     * and returns empty array if no success.
     */
    getBalancesForPartner(partnerId) {
        return this.client.get(`${this.baseUrl}/partner/${partnerId}`, { headers: this.getHeaders(), withCredentials: true })
            .pipe(retryWhen(error => error.pipe(delay(500), take(3))), last(), catchError((error) => {
            this.logger.error(`Http call to get LicenseBalance failed with Status Code: ${error.status}, ${error.statusText}`);
            return of([]);
        }));
    }
    /**
     * Get all the license balances for the partner by product id.
     * retries 3 times, with 500ms delay.
     * and returns empty array if no success.
     */
    getBalancesForPartnerByProduct(partnerId, productId) {
        return this.client.get(`${this.baseUrl}/partner/${partnerId}/product/${productId}`, { headers: this.getHeaders(), withCredentials: true })
            .pipe(retryWhen(error => error.pipe(delay(500), take(3))), last(), catchError((error) => {
            this.logger.error(`Http call to get LicenseBalanceByProduct failed (${error.name}:${error.message})`
                + ` with Status Code: ${error.status}, ${error.statusText}`);
            return of([]);
        }));
    }
    /**
     * Get the license balances for the partner of the product of the license type.
     * retries 3 times, with 500ms delay.
     * and returns empty array if no success.
     */
    getBalanceForPartnerByProductAndLicenseType(partnerId, productId, licenseTypeId) {
        return this.client.get(`${this.baseUrl}/partner/${partnerId}/product/${productId}/licenseType/${licenseTypeId}`, { headers: this.getHeaders(), withCredentials: true })
            .pipe(retryWhen(error => error.pipe(delay(500), take(3))), last(), catchError((error) => {
            this.logger.error(`Http call to get LicenseBalanceByLicenseType failed with Status Code: ${error.status}, ${error.statusText}`);
            return of(null);
        }));
    }
    /**
     * Returns a default set of headers for auth and content
     */
    getHeaders() {
        return new HttpHeaders({
            'Content-Type': 'application/json'
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: LicenseBalanceServiceClient, deps: [{ token: i1$2.HttpClient }, { token: 'LicenseAssignmentServiceBaseUrl' }, { token: 'LicenseBalanceServiceUrlSuffix' }, { token: Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: LicenseBalanceServiceClient, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: LicenseBalanceServiceClient, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$2.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['LicenseAssignmentServiceBaseUrl']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['LicenseBalanceServiceUrlSuffix']
                }] }, { type: Logger }] });

class LicenseBalanceProvider {
    constructor(licenseBalanceServiceClient, logger, cache) {
        this.licenseBalanceServiceClient = licenseBalanceServiceClient;
        this.logger = logger;
        this.cache = cache;
    }
    getBalancesForPartner(partnerId) {
        return this.cache.getOrCreate(this.getCacheKey(partnerId), () => {
            return this.licenseBalanceServiceClient.getBalancesForPartner(partnerId);
        });
    }
    getBalancesForPartnerByProduct(partnerId, productId) {
        return this.cache.getOrCreate(this.getCacheKey(partnerId, productId), () => {
            return this.licenseBalanceServiceClient.getBalancesForPartnerByProduct(partnerId, productId);
        });
    }
    getBalanceForPartnerByProductAndLicenseType(partnerId, productId, licenseTypeId) {
        return this.cache.getOrCreate(this.getCacheKey(partnerId, productId, licenseTypeId), () => {
            return this.licenseBalanceServiceClient.getBalanceForPartnerByProductAndLicenseType(partnerId, productId, licenseTypeId);
        });
    }
    getCacheKey(...params) {
        /* istanbul ignore if: ignore non-happy path on private methods */
        if (params.length < 1) {
            throw new Error('At least one parameter must be provided');
        }
        return `licenseBalance:${params.join(':')}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: LicenseBalanceProvider, deps: [{ token: LicenseBalanceServiceClient }, { token: Logger }, { token: ObservableCacheService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: LicenseBalanceProvider }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: LicenseBalanceProvider, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: LicenseBalanceServiceClient }, { type: Logger }, { type: ObservableCacheService }] });

class UserPermissionsServiceClient {
    constructor(client, baseUrl, urlSuffix, logger) {
        this.client = client;
        this.baseUrl = baseUrl;
        this.urlSuffix = urlSuffix;
        this.logger = logger;
        if (!this.baseUrl) {
            this.baseUrl = 'https://route.skykick.com';
        }
        this.baseUrl = `${this.baseUrl}/${this.urlSuffix}`;
    }
    getPermissions() {
        return this.getPermissionsByCallingEndpoint();
    }
    getPermissionsByAudience(audienceName) {
        return this.getPermissionsByCallingEndpoint(audienceName);
    }
    getPermissionsByCallingEndpoint(audienceName = null) {
        const audiencePortionOfUrl = audienceName ? `?audienceName=${audienceName}` : '';
        return this.client.get(`${this.baseUrl}${audiencePortionOfUrl}`, { headers: this.getHeaders(), withCredentials: true })
            .pipe(retryWhen(error => error.pipe(delay(500), take(3))), last(), catchError((error) => {
            this.logger.error(`Http call to get ${this.baseUrl} failed with Error: ${error.name}, ${error.message}--Status Code: ${error.status}, ${error.statusText}`);
            return of([]);
        }));
    }
    /**
     * Returns a default set of headers for auth and content
     */
    getHeaders() {
        return new HttpHeaders({
            'Content-Type': 'application/json'
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: UserPermissionsServiceClient, deps: [{ token: i1$2.HttpClient }, { token: 'SewrSystemDomain' }, { token: 'PermissionsServiceUrlSuffix' }, { token: Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: UserPermissionsServiceClient, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: UserPermissionsServiceClient, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$2.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['SewrSystemDomain']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['PermissionsServiceUrlSuffix']
                }] }, { type: Logger }] });

/**
 * provides the user permissions where needed.
 *
 * When we implement a way to request that the cache be invalidated,
 * we would invalidate the it here with this.cache.clear();
 */
class UserPermissionsProviderService {
    constructor(userPermissionsServiceClient, cache) {
        this.userPermissionsServiceClient = userPermissionsServiceClient;
        this.cache = cache;
        this.ttl = Number.MAX_SAFE_INTEGER;
        // this is a service/singleton.  Clear the cache on startup.
        // in the future when we have good cache invalidation we may want to change this.
        this.cache.clear();
    }
    getPermissions(user) {
        return this.cache.getOrCreate(this.userToKey(user), () => this.userPermissionsServiceClient.getPermissions(), this.ttl);
    }
    getPermissionsByAudience(user, audienceName) {
        return this.cache.getOrCreate(this.userAndAudienceToKey(user, audienceName), () => this.userPermissionsServiceClient.getPermissionsByAudience(audienceName));
    }
    userAndAudienceToKey(user, audienceName) {
        return `${this.userToKey(user)}_A:${audienceName}`;
    }
    userToKey(user) {
        return `P:${user.partnerId}_U:${user.userId}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: UserPermissionsProviderService, deps: [{ token: UserPermissionsServiceClient }, { token: ObservableCacheService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: UserPermissionsProviderService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: UserPermissionsProviderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: UserPermissionsServiceClient }, { type: ObservableCacheService }] });

/**
 * Service used to retrieve the current user of they system and information about that user.
 */
class AbstractUserProvider {
}

/**
 * Service used to retrieve any impersonation details about the give user
 */
class ISkyKickPartnerPortalUserImpersonationDetailsProvider {
}

/**
 * @inheritdoc
 */
class SkyKickPartnerPortalUserImpersonationDetailsProvider {
    /**
     * @inheritdoc
     */
    getImpersonationDetails(user) {
        return {
            isImpersonation: !!user.extendedProfile['impersonator'],
            impersonator: user.extendedProfile['impersonator']
        };
    }
}

class SkyKickPartnerPortalUser {
    constructor(identityProvider, licenseAssignmentProvider, licenseBalanceProvider, permissionsProviderService) {
        this.identityProvider = identityProvider;
        this.licenseAssignmentProvider = licenseAssignmentProvider;
        this.licenseBalanceProvider = licenseBalanceProvider;
        this.permissionsProviderService = permissionsProviderService;
        this.extendedProfile = {};
        this.extendedProfile['nickname'] = this.identityProvider.getClaim('nickname');
        this.extendedProfile['impersonator'] = this.identityProvider.getClaim('impersonator');
        this.extendedProfile['mfaenabled'] = this.identityProvider.getClaim('mfaenabled');
    }
    get userId() {
        return this.identityProvider.getClaim('contactcrmid');
    }
    get fullName() {
        return `${this.firstName} ${this.lastName}`;
    }
    get firstName() {
        return this.identityProvider.getClaim('firstname');
    }
    get lastName() {
        return this.identityProvider.getClaim('lastname');
    }
    get email() {
        return this.identityProvider.getClaim('name');
    }
    get partnerId() {
        return this.identityProvider.getClaim('partnercrmid');
    }
    get preferredCultureCode() {
        return this.identityProvider.getClaim('preferredculturecode');
    }
    get partnerName() {
        return this.identityProvider.getClaim('partnername');
    }
    get picture() {
        return this.identityProvider.getPicture();
    }
    get assignedLicenses() {
        return this.licenseAssignmentProvider.getLicenses(this.partnerId, this.userId);
    }
    get isCustomer() {
        const roles = this.identityProvider.getClaim('roles');
        var isInRole = false;
        roles.forEach(role => {
            if (role.includes('customer')) {
                isInRole = true;
            }
        });
        return isInRole;
    }
    get isTestPartner() {
        return this.identityProvider.getClaim('istestpartner');
    }
    get isDnsManagerEnabled() {
        return this.identityProvider.getClaim('isdnsmanagerenabled');
    }
    /**
     * @inheritdoc
     */
    hasPermission(permission) {
        const betaFlights = this.identityProvider.getClaim('betaFlights'.toLowerCase());
        const roles = this.identityProvider.getClaim('roles');
        const legacyAccessRole = this.identityProvider.getClaim('legacyAccessRole'.toLowerCase());
        const privileges = this.getFlattenPrivileges();
        return legacyAccessRole === permission ||
            (betaFlights && betaFlights.includes(permission)) ||
            (roles && roles.includes(permission)) ||
            (privileges && privileges.includes(permission));
    }
    hasPermissionObs(permission, audienceName) {
        if (audienceName) {
            return this.includesPermissionString(this.permissionsProviderService.getPermissionsByAudience(this, audienceName), `${audienceName}_${permission}`);
        }
        if (this.hasPermission(permission)) {
            return of(true);
        }
        return this.includesPermissionString(this.permissionsProviderService.getPermissions(this), permission);
        ;
    }
    async hasLicense(productId) {
        // todo: we can go back to using the entire getLicense and then checking or the types
        // because these values are cached.
        // but this leads to the question if, should the license-assignment-provider be refactored
        // so that it uses the cache of the whole thing to return the parts.
        // but, what about the ttl for the cache, and if the licenses were changed but this hasn't been updated?
        return this.licenseBalanceProvider.getBalancesForPartnerByProduct(this.partnerId, productId)
            .pipe(take(1), map(async (balances) => {
            if (balances.some(balance => balance.totalPurchased === 0)) {
                return false;
            }
            // the combining of promise and observable isn't great, but it prevents us from having to
            // change all the license provider code to use observables
            return (await this.assignedLicenses).some(l => l.productId === productId);
        })).toPromise();
    }
    async hasLicenseForProductAndLicenseType(productId, licenseTypeId) {
        var assignedLicensesForUser = await this.assignedLicenses;
        if (!assignedLicensesForUser) {
            return false;
        }
        var assignedLicense = assignedLicensesForUser.find(l => l.licenseTypeId == licenseTypeId);
        if (assignedLicense && assignedLicense.licenseType === LicenseAssignmentType.Feature) {
            return true;
        }
        return this.licenseBalanceProvider.getBalanceForPartnerByProductAndLicenseType(this.partnerId, productId, licenseTypeId)
            .pipe(take(1), map(async (balance) => {
            if (!balance || balance.totalPurchased === 0) {
                return false;
            }
            return assignedLicensesForUser.some(l => l.productId === productId && l.licenseTypeId === licenseTypeId);
        })).toPromise();
    }
    getFlattenPrivileges() {
        const permissions = this.identityProvider.getClaim('privileges');
        const flattenedPermissions = [];
        if (permissions) {
            Object.keys(permissions).forEach(key => {
                const subPerms = permissions[key];
                subPerms.forEach(s => flattenedPermissions.push(`${key}_${s}`));
            });
        }
        return flattenedPermissions;
    }
    includesPermissionString(permissions$, permission) {
        return permissions$.pipe(map(permissions => {
            return permissions.some(p => `${p.Scope}_${p.Name}` === permission);
        }));
    }
}

/**
 * Retrieves the current user based on the JWT token present in the _sk_auth cookie
 */
class SkyKickUserProvider extends AbstractUserProvider {
    constructor(identityProvider, licenseProvider, licenseBalanceProvider, userPermissionsProviderService) {
        super();
        this.identityProvider = identityProvider;
        this.licenseProvider = licenseProvider;
        this.licenseBalanceProvider = licenseBalanceProvider;
        this.userPermissionsProviderService = userPermissionsProviderService;
    }
    /**
     * @inheritdoc
     */
    getCurrentUser() {
        if (!this.identityProvider.isValid()) {
            throw new Error('Invalid user context. Either no user is logged in or their login has expired');
        }
        return new SkyKickPartnerPortalUser(this.identityProvider, this.licenseProvider, this.licenseBalanceProvider, this.userPermissionsProviderService);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: SkyKickUserProvider, deps: [{ token: AbstractIdentityProvider }, { token: LicenseAssignmentProvider }, { token: LicenseBalanceProvider }, { token: UserPermissionsProviderService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: SkyKickUserProvider, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: SkyKickUserProvider, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: AbstractIdentityProvider }, { type: LicenseAssignmentProvider }, { type: LicenseBalanceProvider }, { type: UserPermissionsProviderService }] });

/**
 * SkyKick Auth Module
 *
 * This Angular Module manages all SkyKick Authentication and Authorization by abstracting away
 * the implementation of these 2 pillars of auth and providing application level contracts for
 * querying against a user's identity.  This allows auth to be implemented across the SkyKick
 * platform in a number of different ways without consuming application having to worry about the
 * underlying implementation at all.  An application should simply have to ask questions like
 * "Who am I?" and "What am I allowed to do?" no matter what flavor of auth is used or how it is
 * implemented.
 */
class AuthModule {
    static forRoot(config) {
        return {
            ngModule: AuthModule,
            providers: [
                { provide: HTTP_INTERCEPTORS, useClass: CrossSiteInterceptor, multi: true },
                { provide: AbstractUserProvider, useClass: SkyKickUserProvider },
                { provide: AbstractIdentityProvider, useClass: SkyKickIdentityProvider },
                { provide: ISkyKickPartnerPortalUserImpersonationDetailsProvider, useClass: SkyKickPartnerPortalUserImpersonationDetailsProvider },
                { provide: LicenseAssignmentProvider, useClass: LicenseAssignmentProvider },
                { provide: ILicenseAssignmentServiceClient, useClass: LicenseAssignmentServiceClient },
                { provide: LicenseBalanceProvider, useClass: LicenseBalanceProvider },
                { provide: Logger, useClass: Logger },
                { provide: ObservableCacheService, useClass: ObservableCacheService },
                { provide: 'LicenseAssignmentServiceBaseUrl', useValue: config.LicenseAssignmentServiceBaseUrl },
                { provide: 'LicenseAssignmentServiceUrlSuffix', useValue: 'licenseassignment' },
                { provide: 'LicenseBalanceServiceUrlSuffix', useValue: 'licensebalance' },
                { provide: UserPermissionsServiceClient, useClass: UserPermissionsServiceClient },
                { provide: UserPermissionsProviderService, useClass: UserPermissionsProviderService },
                { provide: 'PermissionsServiceUrlSuffix', useValue: 'pipe/platform-apis/permissions/user' },
                { provide: 'SewrSystemDomain', useValue: config.SewrSystemDomain },
                { provide: JwtHelperService, useClass: JwtHelperService },
                { provide: JWT_OPTIONS, useValue: {} }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: AuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.5", ngImport: i0, type: AuthModule, imports: [CommonModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: AuthModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: AuthModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ]
                }]
        }] });

/**
 * Configuration for identity middleware
 */
class IdentityConfiguration {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: IdentityConfiguration, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: IdentityConfiguration }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.5", ngImport: i0, type: IdentityConfiguration, decorators: [{
            type: Injectable
        }] });

/**
 * Simple model that contains details about the current impersonation status of a give user.
 */
class SkyKickPartnerPortalImpersonationDetails {
}

/*
 * Public API Surface of auth
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AbstractUserProvider, AuthModule, ILicenseAssignmentServiceClient, ISkyKickPartnerPortalUserImpersonationDetailsProvider, IdentityConfiguration, LicenseAssignmentProvider, LicenseAssignmentServiceClient, SkyKickPartnerPortalImpersonationDetails, SkyKickUserProvider };
